import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Lock, CheckCircle, XCircle, MessageSquare, Brain, Sparkles, Check, Unlock } from 'lucide-react';
import { getTranslation } from './languages';
import { getCookie } from './utils/cookies';
import { renderMarkdown } from './utils/markdownUtils';

const REVIEW_SCHEDULE = [1, 3, 7, 14, 30, 60];

const ReviewQuestionModal = ({ isOpen, onClose, tip, language = 'en', userId, initialSlide = 0, onClearReviewDue, onSSEUpdate, isTutorialMode = false, dummyReviewQuestions = [] }) => {
    const t = useCallback((key, params = {}) => getTranslation(key, language, params), [language]);
    
    // Note: renderMarkdown is now imported from utils/markdownUtils.js
    // We create a wrapper to maintain the same function signature
    const renderMarkdownWrapper = (content, isCompact = false, useWhiteText = false) => {
        return renderMarkdown(content, { isCompact, useWhiteText });
    };
    
    // Initialize states for each review day
    const initializeReviewStates = useCallback(() => REVIEW_SCHEDULE.map(() => ({
        selectedAnswer: null,
        confidence: null,
        showResult: false,
        chatMessages: [],
        hasStartedChat: false
    })), []);
    
    const [reviewStates, setReviewStates] = useState(initializeReviewStates());
    const [currentSlide, setCurrentSlide] = useState(initialSlide);
    const [activeTab, setActiveTab] = useState('question'); // 'question' or 'chat'
    const [inputMessage, setInputMessage] = useState('');
    const [showClearChatConfirm, setShowClearChatConfirm] = useState(false);
    const [hasManuallyNavigated, setHasManuallyNavigated] = useState(false);
    
    const modalRef = useRef(null);
    const chatContainerRef = useRef(null);
    
    // Get current review state
    const currentState = reviewStates[currentSlide];

    // State for API data
    const [reviewQuestions, setReviewQuestions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // Reset states when modal closes or tip changes
    useEffect(() => {
        if (!isOpen) {
            // Clean up all states when modal closes
            setReviewStates(initializeReviewStates());
            setReviewQuestions([]);
            setActiveTab('question');
            setInputMessage('');
            setShowClearChatConfirm(false);
            setError(null);
            setIsLoading(false);
        }
    }, [isOpen, initializeReviewStates]);

    // Load review questions when modal opens
    useEffect(() => {
        const loadReviewQuestions = async () => {
            if (!isOpen) return;
            
            // If in tutorial mode, use dummy data
            if (isTutorialMode && dummyReviewQuestions && dummyReviewQuestions.length > 0) {
                setReviewQuestions(dummyReviewQuestions);
                
                // Initialize review states based on dummy data
                const initialStates = dummyReviewQuestions.map((q) => {
                    const chatMessages = (q.chatHistory || []).map(msg => ({
                        type: msg.role === 'user' ? 'user' : 'ai',
                        content: msg.text || msg.content || ''
                    }));
                    
                    return {
                        selectedAnswer: q.selectedAnswer,
                        confidence: q.confidence,
                        showResult: q.answeredAt ? true : false,
                        chatMessages: chatMessages,
                        hasStartedChat: q.chatHistory && q.chatHistory.length > 0
                    };
                });
                setReviewStates(initialStates);
                setIsLoading(false);
                return;
            }
            
            if (!tip?.id) return;
            
            // Reset review questions immediately to prevent showing stale data
            setReviewQuestions([]);
            setReviewStates(initializeReviewStates());
            
            setIsLoading(true);
            setError(null);
            
            try {
                const userIdToUse = userId || getCookie('userId');
                const response = await fetch(`/api/review-questions/${tip.id}`, {
                    headers: {
                        'x-user-id': userIdToUse
                    }
                });
                
                if (!response.ok) {
                    throw new Error(t('failedToLoadReviewQuestions'));
                }
                
                const data = await response.json();
                
                console.log('API Response:', data);
                console.log('Review Questions from API:', data.reviewQuestions);
                
                // Transform API data to match component expectations
                const questions = REVIEW_SCHEDULE.map((days, index) => {
                    const existingQuestion = data.reviewQuestions.find(q => q.reviewDay === days);
                    console.log(`Day ${days}: existingQuestion =`, existingQuestion);
                    
                    if (existingQuestion) {
                        return {
                            day: days,
                            unlocked: existingQuestion.unlocked,
                            question: existingQuestion.question,
                            choices: existingQuestion.choices,
                            correctAnswer: existingQuestion.correctAnswer,
                            explanation: existingQuestion.explanations ? existingQuestion.explanations[existingQuestion.correctAnswer] : '',
                            explanations: existingQuestion.explanations,
                            chatStarters: existingQuestion.chatStarters ? Object.values(existingQuestion.chatStarters) : [],
                            selectedAnswer: existingQuestion.selectedAnswer,
                            confidence: existingQuestion.confidence,
                            wasCorrect: existingQuestion.wasCorrect,
                            answeredAt: existingQuestion.answeredAt,
                            chatHistory: existingQuestion.chatHistory || []
                        };
                    } else {
                        // Question not generated yet
                        return {
                            day: days,
                            unlocked: false, // Not generated = not unlocked
                            question: null,
                            choices: [],
                            correctAnswer: null,
                            explanation: null,
                            explanations: {},
                            chatStarters: [],
                            selectedAnswer: null,
                            confidence: null,
                            wasCorrect: null,
                            answeredAt: null,
                            chatHistory: []
                        };
                    }
                });
                
                console.log('Transformed questions:', questions);
                setReviewQuestions(questions);
                
                // Initialize review states based on loaded data
                const initialStates = questions.map((q, index) => {
                    // Always initialize from database when modal opens
                    // This ensures we have the latest chat history
                    const chatMessages = (q.chatHistory || []).map(msg => ({
                        type: msg.role === 'user' ? 'user' : 'ai',
                        content: msg.text || msg.content || ''
                    }));
                    
                    console.log(`[ReviewModal] Loaded chat history for day ${q.day}:`, chatMessages.length, 'messages');
                    
                    return {
                        selectedAnswer: q.selectedAnswer,
                        confidence: q.confidence,
                        showResult: q.answeredAt ? true : false,
                        chatMessages: chatMessages,
                        hasStartedChat: q.chatHistory && q.chatHistory.length > 0
                    };
                });
                setReviewStates(initialStates);
                
            } catch (error) {
                console.error('Error loading review questions:', error);
                setError(error.message);
                // Set empty questions on error
                setReviewQuestions(REVIEW_SCHEDULE.map(days => ({
                    day: days,
                    unlocked: false,
                    question: null,
                    choices: [],
                    correctAnswer: null,
                    explanation: null,
                    explanations: {},
                    chatStarters: [],
                    selectedAnswer: null,
                    confidence: null,
                    wasCorrect: null,
                    answeredAt: null,
                    chatHistory: []
                })));
            } finally {
                setIsLoading(false);
            }
        };
        
        loadReviewQuestions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, tip?.id, REVIEW_SCHEDULE, userId, isTutorialMode]);

    // Update current slide when initialSlide changes (only if user hasn't manually navigated)
    useEffect(() => {
        if (isOpen && !hasManuallyNavigated) {
            setCurrentSlide(initialSlide);
        }
        // Reset manual navigation flag and slide when modal closes
        if (!isOpen) {
            setHasManuallyNavigated(false);
            setCurrentSlide(initialSlide);
        }
    }, [isOpen, initialSlide, hasManuallyNavigated]);

    // Always reset to question tab when modal opens
    useEffect(() => {
        if (isOpen) {
            setActiveTab('question');
        }
    }, [isOpen]);

    // Check for pending responses when modal reopens
    useEffect(() => {
        const checkPendingRequest = async () => {
            if (!isOpen || !tip?.id || isLoading || isTutorialMode) return;
            
            // Make sure we have loaded the review questions first
            if (reviewQuestions.length === 0) return;
            
            const reviewDay = REVIEW_SCHEDULE[currentSlide];
            const userIdToUse = userId || getCookie('userId');
            
            try {
                const response = await fetch(`/api/review-questions/${tip.id}/${reviewDay}/chat/pending`, {
                    headers: {
                        'x-user-id': userIdToUse
                    }
                });
                
                if (response.ok) {
                    const data = await response.json();
                    if (data.hasPending) {
                        console.log(`[ReviewModal] Found pending review chat request: ${data.requestId}`);
                        console.log(`[ReviewModal] Current slide: ${currentSlide}, Current messages:`, reviewStates[currentSlide]?.chatMessages?.length || 0);
                        
                        // Show loading message if not already showing
                        const currentMessages = reviewStates[currentSlide].chatMessages || [];
                        const lastMessage = currentMessages[currentMessages.length - 1];
                        
                        if (!lastMessage || !lastMessage.isLoading) {
                            console.log(`[ReviewModal] Adding loading message to ${currentMessages.length} existing messages`);
                            const loadingMessage = { type: 'ai', content: '...', isLoading: true };
                            updateCurrentState({
                                chatMessages: [...currentMessages, loadingMessage]
                            });
                        } else {
                            console.log(`[ReviewModal] Loading message already present`);
                        }
                    }
                }
            } catch (error) {
                console.error('Error checking pending request:', error);
            }
        };
        
        if (isOpen && !isLoading) {
            checkPendingRequest();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, currentSlide, tip?.id, isLoading, reviewQuestions]);

    // Auto scroll to bottom when chat messages change
    useEffect(() => {
        if (chatContainerRef.current && activeTab === 'chat') {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    }, [currentState?.chatMessages, activeTab]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!isOpen) return;
            
            if (e.key === 'Escape') {
                onClose();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    // Set up SSE callback for handling review chat updates
    useEffect(() => {
        if (!isOpen || !onSSEUpdate) return;
        
        const handleSSEUpdate = (data) => {
            if (!data || !data.data) return;
            
            const { eventType } = data;
            const updateData = data.data;
            const tipId = updateData?.tipId;
            const reviewDay = updateData?.reviewDay;
            
            // Check if this update is for the current tip and review day
            if (tipId === tip?.id && reviewDay === REVIEW_SCHEDULE[currentSlide]) {
                const slideIndex = REVIEW_SCHEDULE.indexOf(reviewDay);
                if (slideIndex === -1) return;
                
                if (eventType === 'completed' && updateData.response) {
                    console.log(`[ReviewModal SSE] Received response for tip ${tipId}, day ${reviewDay}`);
                    
                    // Play notification sound for new message
                    try {
                        const audio = new Audio('/tip_ready.wav');
                        audio.volume = 0.3; // Quieter volume for chat messages
                        audio.play().catch(error => {
                            console.log('Could not play notification sound:', error);
                        });
                    } catch (error) {
                        console.log('Error creating audio:', error);
                    }
                    
                    // Replace loading message with actual response
                    setReviewStates(prevStates => {
                        const newStates = [...prevStates];
                        // Ensure we have the slide data
                        if (!newStates[slideIndex]) {
                            console.warn('[ReviewModal SSE] Review state not initialized for slide:', slideIndex);
                            return prevStates;
                        }
                        
                        // Get current messages, preserving all non-loading messages
                        const currentMessages = newStates[slideIndex].chatMessages || [];
                        console.log(`[ReviewModal SSE] Current messages before update:`, currentMessages.length);
                        
                        // Find the last user message to determine where to insert the response
                        let lastUserMessageIndex = -1;
                        for (let i = currentMessages.length - 1; i >= 0; i--) {
                            if (currentMessages[i].type === 'user') {
                                lastUserMessageIndex = i;
                                break;
                            }
                        }
                        
                        // Remove any loading messages after the last user message
                        const messages = [];
                        for (let i = 0; i <= lastUserMessageIndex; i++) {
                            messages.push(currentMessages[i]);
                        }
                        
                        // Add any non-loading AI messages that might have come after
                        for (let i = lastUserMessageIndex + 1; i < currentMessages.length; i++) {
                            if (!currentMessages[i].isLoading) {
                                messages.push(currentMessages[i]);
                            }
                        }
                        
                        // Add the new response
                        messages.push({ type: 'ai', content: updateData.response });
                        console.log(`[ReviewModal SSE] Messages after update:`, messages.length);
                        
                        newStates[slideIndex] = {
                            ...newStates[slideIndex],
                            chatMessages: messages
                        };
                        return newStates;
                    });
                } else if (eventType === 'error') {
                    // Replace loading message with error message
                    setReviewStates(prevStates => {
                        const newStates = [...prevStates];
                        // Ensure we have the slide data
                        if (!newStates[slideIndex]) {
                            console.warn('Review state not initialized for slide:', slideIndex);
                            return prevStates;
                        }
                        
                        // Get current messages and remove loading messages intelligently
                        const currentMessages = newStates[slideIndex].chatMessages || [];
                        const messages = currentMessages.filter(msg => !msg.isLoading);
                        
                        // Add error message
                        messages.push({ 
                            type: 'ai', 
                            content: t('errorGeneratingResponse') || 'Sorry, I encountered an error generating a response. Please try again.' 
                        });
                        
                        newStates[slideIndex] = {
                            ...newStates[slideIndex],
                            chatMessages: messages
                        };
                        return newStates;
                    });
                }
            }
        };
        
        // Set the callback
        onSSEUpdate.current = handleSSEUpdate;
        
        // Clean up when component unmounts or modal closes
        return () => {
            if (onSSEUpdate.current === handleSSEUpdate) {
                onSSEUpdate.current = null;
            }
        };
    }, [isOpen, onSSEUpdate, tip?.id, currentSlide, t]);

    const updateCurrentState = (updates) => {
        const newStates = [...reviewStates];
        newStates[currentSlide] = { ...newStates[currentSlide], ...updates };
        setReviewStates(newStates);
    };

    const handleSubmit = async () => {
        if (currentState.selectedAnswer === null || currentState.confidence === null) return;
        
        // In tutorial mode, just show the result without API calls
        if (isTutorialMode) {
            updateCurrentState({ showResult: true });
            return;
        }
        
        try {
            // Save the answer to the database
            const userIdToUse = userId || getCookie('userId');
            const reviewDay = REVIEW_SCHEDULE[currentSlide];
            
            const response = await fetch(`/api/review-questions/${tip.id}/${reviewDay}/answer`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'x-user-id': userIdToUse
                },
                body: JSON.stringify({
                    selectedAnswer: currentState.selectedAnswer,
                    confidence: currentState.confidence
                })
            });
            
            if (!response.ok) {
                console.error('Failed to save answer:', await response.text());
            } else {
                const result = await response.json();
                console.log('Answer saved successfully:', result);
                
                // Update the reviewQuestions state to reflect that this question has been answered
                setReviewQuestions(prevQuestions => {
                    const newQuestions = [...prevQuestions];
                    if (newQuestions[currentSlide]) {
                        newQuestions[currentSlide] = {
                            ...newQuestions[currentSlide],
                            answeredAt: new Date().toISOString(),
                            selectedAnswer: currentState.selectedAnswer,
                            confidence: currentState.confidence,
                            wasCorrect: currentState.selectedAnswer === newQuestions[currentSlide].correctAnswer
                        };
                    }
                    return newQuestions;
                });
                
                // Clear review due state if this is the review that matches the current review count
                // The tip's reviewCount tells us which review is currently due
                // reviewCount = 0 means day 1 review is due (REVIEW_SCHEDULE[0] = 1)
                // reviewCount = 1 means day 3 review is due (REVIEW_SCHEDULE[1] = 3)
                // etc.
                const currentReviewCount = tip?.reviewCount || 0;
                const expectedReviewDayIndex = currentReviewCount < REVIEW_SCHEDULE.length ? currentReviewCount : -1;
                const shouldClearReviewDue = currentSlide === expectedReviewDayIndex;
                
                console.log('[ReviewQuestionModal] Review clear logic:', {
                    currentSlide,
                    currentReviewCount,
                    expectedReviewDayIndex,
                    reviewDay: REVIEW_SCHEDULE[currentSlide],
                    expectedReviewDay: expectedReviewDayIndex >= 0 ? REVIEW_SCHEDULE[expectedReviewDayIndex] : null,
                    shouldClearReviewDue,
                    tipId: tip?.id
                });
                
                // If we just answered the review that matches the current review count, clear the review due state
                if (shouldClearReviewDue && onClearReviewDue && tip) {
                    console.log(`[ReviewQuestionModal] Clearing review due state for tip ${tip.id} (answered day ${REVIEW_SCHEDULE[currentSlide]} review, reviewCount: ${currentReviewCount})`);
                    onClearReviewDue(tip.id);
                }
            }
        } catch (error) {
            console.error('Error saving answer:', error);
        }
        
        // Show the result after saving
        updateCurrentState({ showResult: true });
    };

    const handleStartDeepDive = async () => {
        if (!currentState.hasStartedChat && currentState.selectedAnswer !== null) {
            // Add the pre-generated bot message based on user's choice
            const botMessage = {
                type: 'ai',
                content: reviewQuestions[currentSlide]?.chatStarters?.[currentState.selectedAnswer] || 'Let me help you understand this concept better.'
            };
            updateCurrentState({ 
                chatMessages: [botMessage],
                hasStartedChat: true
            });
            
            // Skip database save in tutorial mode
            if (!isTutorialMode) {
                // Save the initial chat starter to database
                try {
                    const userIdToUse = userId || getCookie('userId');
                    const reviewDay = REVIEW_SCHEDULE[currentSlide];
                    
                    await fetch(`/api/review-questions/${tip.id}/${reviewDay}/chat`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'x-user-id': userIdToUse
                        },
                        body: JSON.stringify({
                            message: botMessage.content,
                            role: 'ai'
                        })
                    });
                } catch (error) {
                    console.error('Error saving initial chat message:', error);
                }
            }
        }
        setActiveTab('chat');
    };

    const handleClearChat = () => {
        if (currentState.chatMessages.length > 0) {
            setShowClearChatConfirm(true);
        } else {
            handleStartDeepDive();
        }
    };

    const confirmClearChat = async () => {
        // Clear from database
        try {
            const userIdToUse = userId || getCookie('userId');
            const reviewDay = REVIEW_SCHEDULE[currentSlide];
            
            await fetch(`/api/review-questions/${tip.id}/${reviewDay}/chat`, {
                method: 'DELETE',
                headers: {
                    'x-user-id': userIdToUse
                }
            });
        } catch (error) {
            console.error('Error clearing chat from database:', error);
        }
        
        updateCurrentState({ 
            chatMessages: [],
            hasStartedChat: false
        });
        setShowClearChatConfirm(false);
        handleStartDeepDive();
    };

    const handleSendMessage = async () => {
        if (!inputMessage.trim()) return;
        
        const userMessage = { type: 'user', content: inputMessage };
        
        // Update UI immediately with user message
        updateCurrentState({
            chatMessages: [...currentState.chatMessages, userMessage]
        });
        
        const messageToSend = inputMessage;
        setInputMessage('');
        
        // In tutorial mode, provide pre-defined responses
        if (isTutorialMode) {
            // Add a pre-defined AI response for tutorial mode
            setTimeout(() => {
                const aiResponse = t('tutorialReviewChatResponse') || 
                    "Great question! In neural networks, the hidden layers are where the actual 'learning' happens. They transform the input data through multiple levels of abstraction, allowing the network to learn complex patterns. Think of it like how your brain processes visual information - first detecting edges, then shapes, then objects, and finally understanding what you're seeing. Each hidden layer learns increasingly sophisticated features!";
                updateCurrentState({
                    chatMessages: [...currentState.chatMessages, userMessage, { type: 'ai', content: aiResponse }]
                });
            }, 500);
            return;
        }
        
        // Add loading message
        const loadingMessage = { type: 'ai', content: '...', isLoading: true };
        updateCurrentState({
            chatMessages: [...currentState.chatMessages, userMessage, loadingMessage]
        });
        
        try {
            const userIdToUse = userId || getCookie('userId');
            const reviewDay = REVIEW_SCHEDULE[currentSlide];
            
            // Call the new generate endpoint - it now returns immediately with a requestId
            const response = await fetch(`/api/review-questions/${tip.id}/${reviewDay}/chat/generate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-user-id': userIdToUse
                },
                body: JSON.stringify({
                    message: messageToSend
                })
            });
            
            if (!response.ok) {
                throw new Error(t('failedToGenerateAIResponse'));
            }
            
            const data = await response.json();
            
            if (data.requestId) {
                console.log(`Review chat request initiated with ID: ${data.requestId}`);
                // Response will come via SSE
            } else if (data.response) {
                // Backwards compatibility - immediate response
                handleChatResponse(currentSlide, userMessage, data.response);
            }
            
        } catch (error) {
            console.error('Error generating chat response:', error);
            
            // Remove loading message and show error
            const messagesWithoutLoading = reviewStates[currentSlide].chatMessages.filter(msg => !msg.isLoading);
            const errorMessage = { type: 'ai', content: t('errorGeneratingResponse') || 'Sorry, I encountered an error generating a response. Please try again.' };
            
            // Update the state for the specific slide
            const newStates = [...reviewStates];
            newStates[currentSlide] = {
                ...newStates[currentSlide],
                chatMessages: [...messagesWithoutLoading, userMessage, errorMessage]
            };
            setReviewStates(newStates);
        }
    };
    
    // Helper function to handle chat response
    const handleChatResponse = useCallback((slideIndex, userMessage, aiResponse) => {
        // Replace loading message with actual AI response
        const aiMessage = { type: 'ai', content: aiResponse };
        const messagesWithoutLoading = reviewStates[slideIndex].chatMessages.filter(msg => !msg.isLoading);
        
        // Update the state for the specific slide
        const newStates = [...reviewStates];
        newStates[slideIndex] = {
            ...newStates[slideIndex],
            chatMessages: [...messagesWithoutLoading, aiMessage]
        };
        setReviewStates(newStates);
    }, [reviewStates]);
    
    // Note: SSE updates for review chat responses are handled at the App.js level
    // This component receives updates through props and doesn't need direct SSE subscription

    // Debug log for tutorial mode backdrop issue (remove after testing)
    useEffect(() => {
        if (isOpen && isTutorialMode) {
            console.log('[ReviewQuestionModal Debug] Modal opened in tutorial mode - backdrop should be visible');
        }
    }, [isOpen, isTutorialMode]);

    if (!isOpen) return null;

    const currentQuestion = reviewQuestions[currentSlide] || {
        day: REVIEW_SCHEDULE[currentSlide],
        unlocked: false,
        question: null,
        choices: [],
        correctAnswer: null,
        explanation: null,
        chatStarters: []
    };
    const isCorrect = currentState?.selectedAnswer === currentQuestion.correctAnswer;

    return (
        <div 
            className={`fixed inset-0 flex items-center justify-center p-4 ${
                isTutorialMode 
                    ? 'z-[65] bg-black bg-opacity-60' // Apply backdrop in tutorial mode like other modals
                    : 'z-50 bg-black bg-opacity-50'
            }`}
            onClick={(e) => {
                // Close modal if clicking on backdrop (not on modal content)
                if (e.target === e.currentTarget && !isTutorialMode) {
                    onClose();
                }
            }}
        >
            <div 
                ref={modalRef}
                className={`bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden ${isTutorialMode ? 'animate-fade-in-up-fast' : ''}`}
                data-tutorial-target="review-question-modal"
            >
                {/* Header */}
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                            <Brain className="w-6 h-6 text-orange-500" />
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                                {t('reviewQuestions')}
                            </h2>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-500 transition-colors"
                        >
                            <X className="w-6 h-6" />
                        </button>
                    </div>
                    
                    {/* Progress indicators */}
                    <div className="flex items-center justify-center gap-2">
                        {REVIEW_SCHEDULE.map((days, index) => {
                            const question = isLoading ? { unlocked: false } : (reviewQuestions[index] || { unlocked: false });
                            const isAnswered = question.answeredAt ? true : false;
                            const isSelected = index === currentSlide;
                            
                            return (
                                <div
                                    key={index}
                                    className={`flex flex-col items-center transition-all ${
                                        isSelected ? 'scale-110' : ''
                                    } ${question.unlocked ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}
                                    onClick={() => {
                                        if (question.unlocked) {
                                            setCurrentSlide(index);
                                            setHasManuallyNavigated(true);
                                        }
                                    }}
                                >
                                    <div className={`w-14 h-14 rounded-full flex items-center justify-center text-xs font-medium transition-all ${
                                        isSelected && isAnswered
                                            ? 'bg-green-500 text-white shadow-lg'
                                            : isSelected && !isAnswered
                                            ? 'bg-orange-500 text-white shadow-lg'
                                            : question.unlocked && isAnswered
                                            ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/50'
                                            : question.unlocked
                                            ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 hover:bg-orange-200 dark:hover:bg-orange-900/50'
                                            : 'bg-gray-100 dark:bg-gray-800 text-gray-400'
                                    }`}>
                                        {question.unlocked ? (
                                            isAnswered ? (
                                                <Check className="w-6 h-6" strokeWidth={3} />
                                            ) : (
                                                <Unlock className="w-5 h-5" />
                                            )
                                        ) : (
                                            <Lock className="w-5 h-5" />
                                        )}
                                    </div>
                                    <div className={`mt-1 text-xs ${
                                        isSelected && isAnswered
                                            ? 'text-green-500 font-semibold'
                                            : isSelected
                                            ? 'text-orange-500 font-semibold'
                                            : 'text-gray-500'
                                    }`}>
                                        {t('reviewDay', { day: index + 1 })}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Tab navigation */}
                <div className="flex border-b border-gray-200 dark:border-gray-700">
                    <button
                        onClick={() => setActiveTab('question')}
                        className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                            activeTab === 'question'
                                ? 'text-orange-600 border-b-2 border-orange-600 bg-orange-50 dark:bg-orange-900/20'
                                : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                        }`}
                    >
                        <div className="flex items-center justify-center gap-2">
                            <Sparkles className="w-4 h-4" />
                            {t('reviewQuestion')}
                        </div>
                    </button>
                    <button
                        onClick={() => setActiveTab('chat')}
                        className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                            activeTab === 'chat'
                                ? 'text-orange-600 border-b-2 border-orange-600 bg-orange-50 dark:bg-orange-900/20'
                                : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                        }`}
                    >
                        <div className="flex items-center justify-center gap-2">
                            <MessageSquare className="w-4 h-4" />
                            {t('deepDiveChat')}
                        </div>
                    </button>
                </div>

                {/* Content area */}
                <div className="h-[60vh] overflow-hidden">
                    {isLoading ? (
                        <div className="flex items-center justify-center h-full">
                            <div className="text-center">
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
                                <p className="text-gray-600 dark:text-gray-400">{t('loading')}...</p>
                            </div>
                        </div>
                    ) : error ? (
                        <div className="flex items-center justify-center h-full">
                            <div className="text-center">
                                <p className="text-red-600 dark:text-red-400 mb-2">{t('errorLoadingQuestions')}</p>
                                <p className="text-sm text-gray-600 dark:text-gray-400">{error}</p>
                            </div>
                        </div>
                    ) : currentQuestion.unlocked ? (
                        <>
                            {activeTab === 'question' ? (
                                <div className="p-6 h-full overflow-y-auto">
                                    {/* Question */}
                                    <div className="mb-6">
                                        <div className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                                            {renderMarkdownWrapper(currentQuestion.question)}
                                        </div>
                                    </div>

                                    {/* Choices */}
                                    <div className="space-y-3 mb-6">
                                        {currentQuestion.choices.map((choice, index) => (
                                            <button
                                                key={index}
                                                onClick={() => !currentState.showResult && updateCurrentState({ selectedAnswer: index })}
                                                disabled={currentState.showResult}
                                                className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                                                    currentState.showResult
                                                        ? index === currentQuestion.correctAnswer
                                                            ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                                                            : index === currentState.selectedAnswer
                                                            ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
                                                            : 'border-gray-200 dark:border-gray-700'
                                                        : currentState.selectedAnswer === index
                                                        ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20'
                                                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                                                }`}
                                            >
                                                <div className="flex items-center justify-between">
                                                    <div className="text-gray-800 dark:text-gray-200 flex-1">
                                                        {renderMarkdownWrapper(choice, true)}
                                                    </div>
                                                    {currentState.showResult && (
                                                        <span className="ml-2 flex-shrink-0">
                                                            {index === currentQuestion.correctAnswer ? (
                                                                <CheckCircle className="w-5 h-5 text-green-500" />
                                                            ) : index === currentState.selectedAnswer ? (
                                                                <XCircle className="w-5 h-5 text-red-500" />
                                                            ) : null}
                                                        </span>
                                                    )}
                                                </div>
                                            </button>
                                        ))}
                                    </div>

                                    {/* Confidence level */}
                                    {!currentState.showResult && (
                                        <div className="mb-6">
                                            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                                                {t('howConfident')}
                                            </h4>
                                            <div className="flex gap-3">
                                                {[
                                                    { level: 'guessing', label: t('totallyGuessing'), emoji: '🤔' },
                                                    { level: 'unsure', label: t('aBitUnsure'), emoji: '😅' },
                                                    { level: 'confident', label: t('confident'), emoji: '😎' }
                                                ].map((conf) => (
                                                    <button
                                                        key={conf.level}
                                                        onClick={() => updateCurrentState({ confidence: conf.level })}
                                                        className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                                                            currentState.confidence === conf.level
                                                                ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20'
                                                                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                                                        }`}
                                                    >
                                                        <div className="text-2xl mb-1">{conf.emoji}</div>
                                                        <div className="text-sm text-gray-700 dark:text-gray-300">
                                                            {conf.label}
                                                        </div>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Result and explanation */}
                                    {currentState.showResult && (
                                        <div className={`p-4 rounded-lg mb-6 ${
                                            isCorrect 
                                                ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800' 
                                                : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                                        }`}>
                                            <div className="flex items-center gap-2 mb-2">
                                                {isCorrect ? (
                                                    <>
                                                        <CheckCircle className="w-5 h-5 text-green-600" />
                                                        <span className="font-semibold text-green-800 dark:text-green-200">
                                                            {t('correctWellDone')}
                                                        </span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <XCircle className="w-5 h-5 text-red-600" />
                                                        <span className="font-semibold text-red-800 dark:text-red-200">
                                                            {t('notQuiteRight')}
                                                        </span>
                                                    </>
                                                )}
                                            </div>
                                            <div className="text-gray-700 dark:text-gray-300 text-sm">
                                                {renderMarkdownWrapper(
                                                    currentQuestion.explanations?.[currentState.selectedAnswer] || 
                                                    currentQuestion.explanation
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* Action buttons */}
                                    <div className="flex gap-3">
                                        {!currentState.showResult ? (
                                            <button
                                                onClick={handleSubmit}
                                                disabled={currentState.selectedAnswer === null || currentState.confidence === null}
                                                className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all ${
                                                    currentState.selectedAnswer !== null && currentState.confidence !== null
                                                        ? 'bg-orange-500 text-white hover:bg-orange-600'
                                                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                                }`}
                                            >
                                                {t('submitAnswer')}
                                            </button>
                                        ) : (
                                            <button
                                                onClick={handleStartDeepDive}
                                                className="flex-1 py-3 px-4 rounded-lg font-medium transition-all bg-orange-500 text-white hover:bg-orange-600 flex items-center justify-center gap-2"
                                            >
                                                <MessageSquare className="w-5 h-5" />
                                                {t('startNewDeepDive')}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className="flex flex-col h-full">
                                    {/* Start conversation button */}
                                    {currentState.selectedAnswer !== null && (
                                        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                                            <button
                                                onClick={handleClearChat}
                                                className="w-full py-2 px-4 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition-colors font-medium flex items-center justify-center gap-2"
                                            >
                                                <MessageSquare className="w-4 h-4" />
                                                {currentState.chatMessages.length > 0 ? t('startNewConversation') : t('startConversation')}
                                            </button>
                                        </div>
                                    )}
                                    
                                    {/* Chat messages */}
                                    <div ref={chatContainerRef} className="flex-1 p-6 overflow-y-auto">
                                        {currentState.chatMessages.length === 0 ? (
                                            <div className="text-center text-gray-500 dark:text-gray-400 mt-8">
                                                <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                                <p>{t('askQuestionsToDeepen')}</p>
                                                {currentState.selectedAnswer === null && (
                                                    <p className="mt-2 text-sm">{t('answerQuestionFirst')}</p>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="space-y-4">
                                                {currentState.chatMessages.map((msg, index) => (
                                                    <div
                                                        key={index}
                                                        className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
                                                    >
                                                        <div className={`max-w-[70%] p-3 rounded-lg ${
                                                            msg.type === 'user'
                                                                ? 'bg-orange-500 text-white'
                                                                : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                                                        } ${msg.isLoading ? 'animate-pulse' : ''}`}>
                                                            {msg.isLoading ? (
                                                                <div className="flex space-x-1">
                                                                    <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                                                                    <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                                                                    <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                                                                </div>
                                                            ) : (
                                                                renderMarkdownWrapper(msg.content, false, msg.type === 'user')
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {/* Chat input */}
                                    <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                                        <div className="flex gap-3">
                                            <input
                                                type="text"
                                                value={inputMessage}
                                                onChange={(e) => setInputMessage(e.target.value)}
                                                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                                                placeholder={t('askAboutReviewQuestion')}
                                                className="flex-1 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                                            />
                                            <button
                                                onClick={handleSendMessage}
                                                className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
                                            >
                                                <MessageSquare className="w-5 h-5" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="flex items-center justify-center h-full">
                            <div className="text-center">
                                <Lock className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                                <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                    {t('reviewLocked')}
                                </h3>
                                <p className="text-gray-500 dark:text-gray-400">
                                    {t('reviewWillUnlock', { day: currentQuestion.day })}
                                </p>
                            </div>
                        </div>
                    )}
                </div>

                
                {/* Clear Chat Confirmation Modal */}
                {showClearChatConfirm && (
                    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-xl">
                        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-sm mx-4">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                                {t('clearChatHistory')}
                            </h3>
                            <p className="text-gray-600 dark:text-gray-400 mb-4">
                                {t('clearChatMessage')}
                            </p>
                            <div className="flex gap-3">
                                <button
                                    onClick={confirmClearChat}
                                    className="flex-1 py-2 px-4 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-medium"
                                >
                                    {t('clearAndStartNew')}
                                </button>
                                <button
                                    onClick={() => setShowClearChatConfirm(false)}
                                    className="flex-1 py-2 px-4 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors font-medium"
                                >
                                    {t('cancel')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ReviewQuestionModal;